'use client';

import * as React from 'react';
import { resolveGapWithDefault } from '@federa/utils/styles/spacing-utils';
import { spacing } from '../css-refs.spacing.ts';
import type { StackProps } from '@federa/types/ui-stack';

// BOTH MAPS carry `satisfies Record<NonNullable<StackProps[...]>, string>` (added 2026-09-04) rather than the bare
// `as const` they had. The index at the call site already made a MISSING key an error; the open direction was the other
// one -- an EXTRA key survived any narrowing of the prop union silently, which is how `Text.tsx`'s sibling `WEIGHT_MAP`
// came to need a hand-deletion no gate could ask for. `satisfies` keeps the literal value types `as const` gives while
// turning an orphan into a TS2353.
/** The three EDGE positions both axes share, and the flex keyword each one means on either axis. */
const EDGE_POSITIONS = {
	start: 'flex-start',
	center: 'center',
	end: 'flex-end',
} as const;

const alignMap = {
	...EDGE_POSITIONS,
	stretch: 'stretch',
	baseline: 'baseline',
} as const satisfies Record<NonNullable<StackProps['align']>, string>;

const justifyMap = {
	...EDGE_POSITIONS,
	'space-between': 'space-between',
	'space-around': 'space-around',
	'space-evenly': 'space-evenly',
} as const satisfies Record<NonNullable<StackProps['justify']>, string>;

function insertDividersWithSpacing(
	children: React.ReactNode,
	options: {
		divider: React.ReactNode;
		isHorizontal: boolean;
		gapValue: string | number;
	},
): React.ReactNode[] {
	const { divider, isHorizontal, gapValue } = options;
	const childrenArray = React.Children.toArray(children);
	const result: React.ReactNode[] = [];

	for (const [index, child] of childrenArray.entries()) {
		result.push(child);

		// Add divider between children (not after the last child)
		if (index < childrenArray.length - 1) {
			result.push(
				<div
					key={`divider-${index}`}
					style={{
						[isHorizontal ? 'marginLeft' : 'marginTop']: gapValue,
						[isHorizontal ? 'marginRight' : 'marginBottom']: gapValue,
					}}
				>
					{divider}
				</div>,
			);
		}
	}

	return result;
}

export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
	(
		{
			direction = 'vertical',
			gap = 'md',
			align = 'stretch',
			justify = 'start',
			wrap = false,
			divider,
			fullWidth = false,
			fullHeight = false,
			children,
			className,
			style,
			...props
		},
		ref,
	) => {
		const isHorizontal = direction === 'horizontal';
		const gapValue = resolveGapWithDefault(gap, spacing.md);

		const stackStyles: React.CSSProperties = {
			display: 'flex',
			flexDirection: isHorizontal ? 'row' : 'column',
			alignItems: alignMap[align],
			justifyContent: justifyMap[justify],
			gap: divider ? undefined : gapValue,
			flexWrap: wrap ? 'wrap' : 'nowrap',
			width: fullWidth ? '100%' : undefined,
			height: fullHeight ? '100%' : undefined,
			...style,
		};

		const content = divider
			? insertDividersWithSpacing(children, {
					divider,
					isHorizontal,
					gapValue,
				})
			: children;

		return (
			<div ref={ref} style={stackStyles} className={className} {...props}>
				{content}
			</div>
		);
	},
);

Stack.displayName = 'Stack';

export const HStack = React.forwardRef<HTMLDivElement, Omit<StackProps, 'direction'>>((props, ref) => (
	<Stack ref={ref} direction="horizontal" {...props} />
));

HStack.displayName = 'HStack';

export const VStack = React.forwardRef<HTMLDivElement, Omit<StackProps, 'direction'>>((props, ref) => (
	<Stack ref={ref} direction="vertical" {...props} />
));

VStack.displayName = 'VStack';
